/* eslint-disable camelcase */
import React, { useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Grid2, Stack } from '@mui/material'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import DtoAnswer from 'installation/components/campaign/dto/DtoAnswer'
import { DANGER_ELEMENT_TYPE } from 'installation/constants/InstallationConstants'
import { orderBy } from 'lodash'
import { useDispatch } from 'react-redux'
import i18n from 'simple-react-i18n'
import Question from './Question'
import { CardTitle } from 'components/card/NewCard'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import Input from 'components/forms/Input'
import ProgressBar from 'components/progress/ProgressBar'
import { STATION_TYPE_CONSTANT } from 'station/constants/StationConstants'
import { push } from '@lagunovsky/redux-react-router'
import { normalizeString } from 'utils/StringUtil'
import Checkbox from 'components/forms/Checkbox'
import { isDanger } from 'pgsse/util/PgsseUtil'
import { lightGrey, mainWhite, mediumGrey, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import DtoPgsseForm from 'pgsse/dto/DtoPgsseForm'
import DtoPgsseSurvey from 'pgsse/dto/DtoPgsseSurvey'
import DtoCMSParagraph from 'events/dto/survey/DtoCMSParagraph'

const useStyles = makeStyles(() => ({
    root: {
        minHeight: 35,
        maxHeight: 35,
        '&.Mui-expanded': {
            minHeight: 45,
            maxHeight: 45,
        },
    },
}))

const FilterFields = ({
    setFilter,
}) => {
    const [search, setSearch] = useState('')
    const [noAns, setNoAns] = useState(false)
    const [errQ, setErrQ] = useState(false)

    const onSearch = value => setFilter({ search: normalizeString(value), noAns, errQ })
    return (
        <Card>
            <CardContent>
                <Grid2 container alignItems='center' spacing={1} justifyContent='space-between'>
                    <Grid2 size={4}>
                        <Input
                            title={i18n.search}
                            value={search}
                            onChange={setSearch}
                            onEnterKeyPress={onSearch}
                        />
                    </Grid2>
                    <Grid2>
                        <Checkbox
                            label={'Questions sans réponses'}
                            checked={noAns}
                            onChange={setNoAns}
                        />
                    </Grid2>
                    <Grid2>
                        <Checkbox
                            label={'Questions en erreur'}
                            checked={errQ}
                            onChange={setErrQ}
                        />
                    </Grid2>
                    <Grid2 container justifyContent='flex-end'>
                        <Button variant='contained' onClick={() => onSearch(search)}>
                            {i18n.search}
                        </Button>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    )
}

FilterFields.propTypes= {
    setFilter: PropTypes.func,
}

const VisitFields = ({
    title,
    form = {},
    readMode,
    onChangeForm,
    onSave,
}) => {
    const dispatch = useDispatch()
    return (
        <Card>
            <CardTitle title={title} />
            <CardContent>
                <Grid2 container spacing={1}>
                    <Grid2 size={3}>
                        <SimpleDatePicker
                            label={i18n.visitDate}
                            value={form.formDate}
                            onChange={v => onChangeForm({ formDate: v })}
                            disabled={readMode}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Input
                            title={i18n.mainTechnician}
                            value={form.technicianLogin}
                            onChange={v => onChangeForm({ technicianLogin: v })}
                            disabled={readMode}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Input
                            title={i18n.otherTechnicians}
                            value={form.otherTechnicians}
                            onChange={v => onChangeForm({ otherTechnicians: v })}
                            disabled={readMode}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Input
                            title={i18n.participants}
                            value={form.participants}
                            onChange={v => onChangeForm({ participants: v })}
                            disabled={readMode}
                        />
                    </Grid2>
                    <Grid2 size={12}>
                        <SimpleTextArea
                            title={i18n.comment}
                            value={form.comments}
                            onChange={v => onChangeForm({ comments: v })}
                            disabled={readMode}
                        />
                    </Grid2>
                    <Grid2 size={12} container justifyContent='flex-end' spacing={2}>
                        <Grid2>
                            <Button
                                variant='contained'
                                onClick={() => {
                                    if (form.stationType === STATION_TYPE_CONSTANT.CONTRIBUTOR) {
                                        dispatch(push(`/referencial/contributor/${form.code}/dashboard`))
                                    } else if (form.stationType === STATION_TYPE_CONSTANT.INSTALLATION) {
                                        dispatch(push(`/station/installation/${form.code}/description`))
                                    }
                                }}
                            >
                                {i18n.accessToTheFile}
                            </Button>
                        </Grid2>
                        <Grid2>
                            <Button
                                variant='contained'
                                onClick={() => onSave({ finaliseForm: true })}
                            >
                                {i18n.finalized}
                            </Button>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    )
}

VisitFields.propTypes = {
    title: PropTypes.string,
    form: PropTypes.shape({}), // DtoPgsseForm
    readMode: PropTypes.bool,
    onChangeForm: PropTypes.func,
    onSave: PropTypes.func,
}

const Paragraph = ({
    index,
    paragraph = {},
    form = {},
    onChangeForm,
    expanded,
    setExpanded,
    readMode,
    questions = [],
    answers = [],
}) => {
    const classes = useStyles()

    const nbAnswers = answers.filter(a => !!a.idQuestion).length
    const paragraphAnswer = answers.find(a => !a.idQuestion)

    const bckgroundColor = (() => {
        if (paragraphAnswer?.answerBool) {
            return lightGrey
        }
        if (paragraph.link_questions.length > 0 && paragraph.link_questions.length === nbAnswers) {
            return PASTEL_COLOR.green
        }
        return mediumGrey
    })()

    return (
        <Accordion
            expanded={expanded === `panel${index}`}
            onChange={(_, isExpanded) => setExpanded(isExpanded ? `panel${index}` : false)}
        >
            <AccordionSummary
                sx={{ color: paragraphAnswer?.answerBool ? 'black' : mainWhite, fontSize: '16px' }}
                classes={classes}
                iconColor={paragraphAnswer?.answerBool ? 'black' : mainWhite}
                color={bckgroundColor}
            >
                {`${paragraph.description} (${paragraphAnswer?.answerBool && i18n.irrelevant || `${paragraph.link_questions.length} ${i18n.questions} - ${nbAnswers} ${i18n.answers}`})`}
            </AccordionSummary>
            <AccordionDetails>
                <>
                    <Grid2 container alignItems='center' sx={{ margin: '10 0' }}>
                        <Grid2 size={3}>
                            <Checkbox
                                label={i18n.withoutObject}
                                checked={paragraphAnswer?.answerBool}
                                onChange={v => {
                                    onChangeForm({
                                        link_answers: [
                                            ...form.link_answers.filter(la => !(la.idParagraph === paragraph.idParagraph && !la.idQuestion)),
                                            new DtoAnswer({
                                                ...paragraphAnswer,
                                                recipientCode: form.idInstallation,
                                                idCampaign: form.idCampaign,
                                                recipientType: DANGER_ELEMENT_TYPE.INSTALLATION,
                                                idSurvey: form.idSurvey,
                                                idVisit: form.idVisit,
                                                ...paragraph,
                                                answerBool: v,
                                            }),
                                        ],
                                    })
                                }}
                                disabled={readMode}
                            />
                        </Grid2>
                        <Grid2 size={9}>
                            <SimpleTextArea
                                title={i18n.generalComment}
                                value={paragraphAnswer?.answerText}
                                rows={2}
                                onChange={v => {
                                    onChangeForm({
                                        link_answers: [
                                            ...form.link_answers.filter(la => !(la.idParagraph === paragraph.idParagraph && !la.idQuestion)),
                                            new DtoAnswer({
                                                ...paragraphAnswer,
                                                recipientCode: form.idInstallation,
                                                idCampaign: form.idCampaign,
                                                recipientType: DANGER_ELEMENT_TYPE.INSTALLATION,
                                                idSurvey: form.idSurvey,
                                                idVisit: form.idVisit,
                                                ...paragraph,
                                                answerText: v,
                                            }),
                                        ],
                                    })
                                }}
                                disabled={readMode}
                            />
                        </Grid2>
                    </Grid2>
                    {!paragraphAnswer?.answerBool &&
                        orderBy(paragraph.link_questions, 'order').map(q =>
                            questions.some(fq => fq.idQuestion === q.idQuestion) && (
                                <Question
                                    key={q.idQuestion}
                                    question={q}
                                    dataForm={form}
                                    onChange={onChangeForm}
                                    readMode={readMode}
                                />
                            ),
                        )
                    }
                </>
            </AccordionDetails>
        </Accordion>
    )
}

Paragraph.propTypes = {
    index: PropTypes.number,
    paragraph: PropTypes.shape({}), // DtoCMSParagraph
    form: PropTypes.shape({}), // DtoPgsseForm
    onChangeForm: PropTypes.func,
    expanded: PropTypes.string,
    setExpanded: PropTypes.func,
    readMode: PropTypes.bool,
    questions: PropTypes.arrayOf(PropTypes.shape({})), // DtoCMSQuestion
    answers: PropTypes.arrayOf(PropTypes.shape({})), // DtoAnswer
}

const ParagraphWrapper = ({
    paragraphs,
    form,
    filter,
    onChangeForm,
    readMode,
}) => {
    const [expanded, setExpanded] = useState('panel0')

    return paragraphs.map((paragraph, index) => {
        const answers = form.link_answers.filter(la => la.idParagraph === paragraph.idParagraph)
        const searchFiltered = filter.search ?
            paragraph.link_questions.filter(question => normalizeString(question.description).includes(filter.search)) :
            paragraph.link_questions

        const filteredQuestions = searchFiltered.filter(sf => {
            const answer = answers.find(a => a.idQuestion === sf.idQuestion) || {}
            if (filter.errQ && filter.noAns) {
                return isDanger(answer) || !answer.idQuestion
            } else if (filter.errQ) {
                return isDanger(answer)
            } else if (filter.noAns) {
                return !answer.idQuestion
            }
            return true
        })
        if (!filteredQuestions.length) return null
        return (
            <Paragraph
                index={index}
                paragraph={paragraph}
                form={form}
                onChangeForm={onChangeForm}
                expanded={expanded}
                setExpanded={setExpanded}
                readMode={readMode}
                questions={filteredQuestions}
                answers={answers}
                key={`paragraph_${index}`}
            />
        )
    })
}

ParagraphWrapper.propTypes = {
    paragraphs: PropTypes.arrayOf(PropTypes.instanceOf(DtoCMSParagraph)),
    form: PropTypes.instanceOf(DtoPgsseForm),
    filter: PropTypes.shape({}),
    onChangeForm: PropTypes.func,
    readMode: PropTypes.bool,
}

const Form = ({
    title = '',
    form,
    setForm,
    survey,
    isFormLoaded,
    isSurveyLoaded,
    readMode,
    onSave,
}) => {
    const [filter, setFilter] = useState({})

    const onChangeForm = values => setForm(prev => ({ ...prev, ...values }))

    return (
        <Stack spacing={2}>
            {isFormLoaded && (
                <VisitFields
                    title={title}
                    form={form}
                    readMode={readMode}
                    onChangeForm={onChangeForm}
                    onSave={onSave}
                />
            )}
            <FilterFields setFilter={setFilter}/>
            {
                isSurveyLoaded
                    && <ParagraphWrapper
                        paragraphs={survey.link_paragraphs}
                        form={form}
                        filter={filter}
                        onChangeForm={onChangeForm}
                        readMode={readMode}
                    />
                    || <ProgressBar indeterminate />
            }
        </Stack >
    )
}

Form.propTypes = {
    title: PropTypes.string,
    form: PropTypes.instanceOf(DtoPgsseForm),
    survey: PropTypes.instanceOf(DtoPgsseSurvey),
    isFormLoaded: PropTypes.bool,
    isSurveyLoaded: PropTypes.bool,
    readMode: PropTypes.bool,
    onSave: PropTypes.func,
    setForm: PropTypes.func,
}

export default Form
